import { forwardRef, useState } from "react";
import { shopifyImageUrl, shopifyImageSrcSet } from "@/lib/shopify-image";
import {
  SHOPIFY_IMAGE_VARIANTS,
  type ShopifyImageVariant,
} from "@/lib/shopify-image-presets";

type BaseImgProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet" | "sizes" | "loading" | "width" | "height"
>;

export interface ShopifyImageProps extends BaseImgProps {
  /** Shopify CDN URL (or any other image URL). */
  src: string | null | undefined;
  /** Required alt text. Pass "" for purely decorative images. */
  alt: string;
  /** Picks width list, fallback `sizes`, and intrinsic dimensions. */
  variant: ShopifyImageVariant;
  /** Override the `sizes` attribute. Defaults to the variant's fallback. */
  sizes?: string;
  /** LCP candidate: eager + fetchpriority=high. */
  priority?: boolean;
  /** Force eager loading without bumping fetchpriority (e.g. adjacent slides). */
  eager?: boolean;
  /** Override intrinsic width/height (e.g. when the source aspect is known). */
  width?: number;
  height?: number;
  /**
   * Optional fallback srcset for non-Shopify URLs (e.g. vite-imagetools
   * static imports). Used only when `src` is not a Shopify CDN URL.
   */
  fallbackSrcSet?: string;
  /** Fade in once the image decodes. Defaults to false for simplicity. */
  fade?: boolean;
}

/**
 * Single source of truth for every Shopify product image in the app.
 *
 * Behaves like Liquid's `{{ image | image_tag }}` — pick a variant and
 * the component picks the right widths, `srcset`, `sizes`, loading
 * strategy, and intrinsic dimensions. All width transforms go through
 * the native Shopify CDN (`?width=…`).
 */
const ShopifyImage = forwardRef<HTMLImageElement, ShopifyImageProps>(
  (
    {
      src,
      alt,
      variant,
      sizes,
      priority = false,
      eager = false,
      width,
      height,
      fallbackSrcSet,
      fade = false,
      className,
      onLoad,
      onError,
      ...rest
    },
    ref,
  ) => {
    const config = SHOPIFY_IMAGE_VARIANTS[variant];
    const [loaded, setLoaded] = useState(false);

    if (!src) return null;

    const resolvedSrc =
      shopifyImageUrl(src, { width: config.defaultWidth }) || src;
    const shopifySrcSet = config.noSrcSet
      ? undefined
      : shopifyImageSrcSet(src, config.widths) || undefined;
    const resolvedSrcSet = shopifySrcSet || fallbackSrcSet || undefined;
    const resolvedSizes = config.noSrcSet ? undefined : sizes || config.fallbackSizes;

    const loading: "eager" | "lazy" =
      priority || eager ? "eager" : config.defaultLoading;

    const fetchPriority = priority ? "high" : undefined;

    const fadeClass = fade
      ? `transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`
      : "";

    return (
      <img
        ref={ref}
        src={resolvedSrc}
        srcSet={resolvedSrcSet}
        sizes={resolvedSizes}
        alt={alt}
        loading={loading}
        decoding="async"
        {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
        width={width ?? config.intrinsic.width}
        height={height ?? config.intrinsic.height}
        className={[className, fadeClass].filter(Boolean).join(" ") || undefined}
        onLoad={(e) => {
          setLoaded(true);
          onLoad?.(e);
        }}
        onError={(e) => {
          setLoaded(true);
          onError?.(e);
        }}
        {...rest}
      />
    );
  },
);
ShopifyImage.displayName = "ShopifyImage";

export default ShopifyImage;